import React, { useEffect, useState } from "react";

// On-brand replacement for the browser's window.alert / window.confirm.
//
// Both are promise-based and callable from anywhere:
//   await webAlert("Saved!")
//   if (await webConfirm("Delete?", { destructive: true })) { ... }
// Mount <WebDialogHost/> once near the app root (see WebShell) so the modal
// has somewhere to render. Requests queue, so two calls in a row are shown
// one after another instead of clobbering each other.

type Req = {
  id: number;
  title?: string;
  message: string;
  kind: "alert" | "confirm";
  confirmLabel: string;
  cancelLabel: string;
  destructive: boolean;
  resolve: (ok: boolean) => void;
};

let queue: Req[] = [];
let subs: Array<() => void> = [];
let seq = 0;
const notify = () => subs.forEach((s) => s());

function enqueue(r: Omit<Req, "id">) {
  queue = [...queue, { ...r, id: ++seq }];
  notify();
}

export function webAlert(
  message: string,
  opts: { title?: string; confirmLabel?: string } = {},
): Promise<void> {
  return new Promise((resolve) => {
    enqueue({
      title: opts.title,
      message,
      kind: "alert",
      confirmLabel: opts.confirmLabel ?? "OK",
      cancelLabel: "Cancel",
      destructive: false,
      resolve: () => resolve(),
    });
  });
}

export function webConfirm(
  message: string,
  opts: {
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
  } = {},
): Promise<boolean> {
  return new Promise((resolve) => {
    enqueue({
      title: opts.title,
      message,
      kind: "confirm",
      confirmLabel: opts.confirmLabel ?? "OK",
      cancelLabel: opts.cancelLabel ?? "Cancel",
      destructive: opts.destructive ?? false,
      resolve,
    });
  });
}

export function WebDialogHost() {
  const [, force] = useState(0);
  useEffect(() => {
    const s = () => force((n) => n + 1);
    subs.push(s);
    return () => {
      subs = subs.filter((x) => x !== s);
    };
  }, []);

  const cur = queue[0];
  const settle = (ok: boolean) => {
    if (!cur) return;
    cur.resolve(cur.kind === "confirm" ? ok : true);
    queue = queue.slice(1);
    notify();
  };

  // Enter = confirm, Esc = cancel — parity with native dialogs.
  useEffect(() => {
    if (!cur) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") settle(false);
      else if (e.key === "Enter") settle(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur?.id]);

  if (!cur) return null;

  return (
    <div
      onClick={() => settle(false)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(20,12,4,0.45)",
        display: "grid",
        placeItems: "center",
        padding: 24,
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        className="fadeup"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 22,
          padding: "26px 26px 22px",
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 30px 70px rgba(0,0,0,0.35)",
        }}
      >
        {cur.title && (
          <div
            style={{
              fontFamily: "var(--display)",
              fontSize: 22,
              fontWeight: 900,
              color: "var(--ink)",
              marginBottom: 8,
            }}
          >
            {cur.title}
          </div>
        )}
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 1.5,
            color: cur.title ? "var(--ink-2)" : "var(--ink)",
          }}
        >
          {cur.message}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 22,
          }}
        >
          {cur.kind === "confirm" && (
            <button
              onClick={() => settle(false)}
              style={{
                border: 0,
                borderRadius: 999,
                padding: "11px 20px",
                background: "var(--bg-warm)",
                color: "var(--ink)",
                fontWeight: 800,
                fontSize: 14,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {cur.cancelLabel}
            </button>
          )}
          <button
            autoFocus
            onClick={() => settle(true)}
            style={{
              border: 0,
              borderRadius: 999,
              padding: "11px 22px",
              background: cur.destructive ? "#E5484D" : "var(--primary)",
              color: "#fff",
              fontWeight: 800,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: "inherit",
              boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.15)",
            }}
          >
            {cur.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WebDialogHost;
